import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Share2, FileDown } from "lucide-react";
import { api } from "../lib/api";
import { apiError, useAuth } from "../lib/auth";
import { PageHeader, NeonButton } from "../components/common";
import { Wizard, Progress } from "../components/connect/Wizard";
import { CopyBlock } from "../components/connect/Bits";

const download = async (url, name) => {
  const r = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
};

export default function Connect() {
  const { isOwner } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState(null);
  const load = useCallback((deep = false) => api.get("/connect/status", { params: { deep } }).then((r) => setD(r.data)), []);
  useEffect(() => { load(); const t = setInterval(() => load(), 15000); return () => clearInterval(t); }, [load]);
  const recheck = async () => { setBusy(true); try { await load(true); toast.success("Live checks finished"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const makeShare = async () => { try { const { data } = await api.post("/connect/share"); setShare(data); } catch (e) { toast.error(apiError(e)); } };
  if (!d) return <p className="font-mono text-xs text-white/40">loading setup…</p>;
  return (
    <div className="max-w-4xl space-y-6 pb-24" data-testid="connect-page">
      <PageHeader eyebrow="// connect your systems · one place, no guesswork" title="Connect & Setup">
        <NeonButton variant="ghost" onClick={recheck} disabled={busy} data-testid="setup-recheck"><RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Run live checks</NeonButton>
        <NeonButton variant="ghost" onClick={() => download("/connect/pdf", "aegis-setup-guide.pdf")} data-testid="setup-pdf"><FileDown className="w-3 h-3" /> PDF</NeonButton>
        {isOwner && <NeonButton variant="ghost" onClick={makeShare} data-testid="setup-share"><Share2 className="w-3 h-3" /> Share guide</NeonButton>}
      </PageHeader>
      <div className="panel p-5 space-y-3">
        <p className="text-sm text-white/70">Every value below is already filled in for <b className="text-white">{d.values.merchant}</b> and points at the live production address <code className="text-brand" data-testid="setup-base-url">{d.values.base_url}</code>. Work top to bottom; each step turns green on its own when AEGIS sees it working. Everything can be done right here with no developer: steps marked optional are only for stores that embed on their own site.</p>
        <Progress done={d.done} total={d.total} />
      </div>
      {share && <div className="panel p-5 space-y-2 border-brand/40" data-testid="setup-share-result">
        <p className="text-sm">Read-only link you can share with a teammate or developer if you want help. It shows only public setup values (no keys or secrets) and expires {new Date(share.expires_at).toLocaleDateString()}.</p>
        <CopyBlock text={share.url} testid="copy-share-url" label="Link" />
      </div>}
      <Wizard d={d} edit={isOwner} setD={setD} reload={() => load()} />
    </div>
  );
}
